import {
  Alert,
  Badge,
  Button,
  Card,
  CardBody,
  Col,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  FormGroup,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
  UncontrolledDropdown,
} from "reactstrap";
import React, { useContext, useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import PageHeader from "../common/PageHeader";
import FalconCardHeader from "../common/FalconCardHeader";
import withRedirect from "../../helpers/hoc/withRedirect";
import ButtonIcon from "../common/ButtonIcon";
import { faPlus, faToggleOn } from "@fortawesome/free-solid-svg-icons";
import { useParams } from "react-router";
import useSWRFetchPagination from "../../helpers/hooks/useSWRFetchPagination";
import {
  activateAllReserveFundPayments,
  addReserveFundBalance,
  fetchReserveFund,
  toggleReserveFundPaymentStatus,
} from "../../helpers/api/finance";
import EditReserveFundAmountModal from "./EditReserveFundAmountModal";
import { Toast } from "../UI/toast/Toast";
import { SUCCESS_RESPONSE } from "../../helpers/codeResponses/codes";
import Swal from "sweetalert2";
import handleErrorMessage from "../../helpers/errors/handleErrorMessage";
import { isIterableArray } from "../../helpers/utils";
import FullPagination from "../common/pagination/FullPagination";
import { useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { PreloadState } from "../UI/preloading/PreloadState";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import SupportLink from "../root/support/components/SupportLink";
import { Fragment } from "react";
import NumberFormat from "react-number-format";
import { useHistory } from "react-router-dom/cjs/react-router-dom.min";
import moment from "moment";
import { CurrencyContext } from "../../context/Context";
import Flex from "../common/Flex";
import Filter from "../common/Filter";
import { findAllCurrencies } from "../../helpers/api/currency";
import useExchangeRateForDate from "../../helpers/hooks/useExchangeRateForDate";
import InputWithLeftSelect from "../common/CurrencyAmountInput";
import {
  convertToUsd,
  formatAmountRounded,
  getCurrencyName,
  getCurrencySymbol,
} from "../../helpers/currencyConversion";
import { fetchAllPages } from "../../helpers/api/pagination";

const resolveCurrencySymbol = (currency, fallbackCurrency) => {
  const name = (currency?.name || "").toLowerCase();
  const iso = (currency?.iso || "").toLowerCase();

  if (currency?.symbol) return currency.symbol;
  if (name.includes("boliva") || iso === "ves" || iso === "bs") return "Bs";
  if (currency?.iso) return getCurrencySymbol(currency.iso);
  return fallbackCurrency?.symbol || "$";
};

const ReserveFund = ({
  setRedirect,
  setRedirectUrl,
  onEditAction,
  options,
  alternativeName,
  showPagination = true,
  ...rest
}) => {
  const { id } = useParams();
  const { selectedCondominium } = useSelector((state) => state.condominium);

  const [collapseOne, collapseOneOpen] = useState(false);
  const [balanceModal, setBalanceModal] = useState(false);
  const [editingPayment, setEditingPayment] = useState(null);
  const [selectedFund, setSelectedFund] = useState(null);
  const [balanceData, setBalanceData] = useState({
    amount: "",
    description: "",
  });
  const [availableCurrencies, setAvailableCurrencies] = useState([]);
  const [selectedCurrencyId, setSelectedCurrencyId] = useState("");
  const [hasLoadedCurrencies, setHasLoadedCurrencies] = useState(false);
  const history = useHistory();
  const [params, setParams] = useState({
    active: "",
    condominiumId: id,
    start_date: null,
    end_date: null,
  });
  const {
    data,
    error,
    isLoading,
    meta,
    mutate,
    handler: { nextPage, previousPage, perPage, goToPage },
  } = useSWRFetchPagination(fetchReserveFund, params);

  const { currency: curr, baseCurrency } = useContext(CurrencyContext);
  const activeExchangeRate = Number(curr?.value ?? 0);
  const currentDate = useMemo(() => moment(), []);

  useEffect(() => {
    let isMounted = true;

    const loadCurrencies = async () => {
      if (!selectedCondominium) return;

      setHasLoadedCurrencies(false);
      try {
        const response = await fetchAllPages((page, pageSize) => findAllCurrencies(page, pageSize, {
          condominium_id: selectedCondominium,
        }));
        if (isMounted) setAvailableCurrencies(response?.data || []);
      } catch (error) {
        if (isMounted) handleErrorMessage(error);
      } finally {
        if (isMounted) setHasLoadedCurrencies(true);
      }
    };

    loadCurrencies();

    return () => {
      isMounted = false;
    };
  }, [selectedCondominium]);

  const currencyCatalog = useMemo(() => {
    const normalized = (availableCurrencies || []).map((item) => ({
      ...item,
      id: String(item.id),
      iso: (item?.iso || "").toUpperCase(),
      isFallbackBase: false,
    }));

    const baseIso = (baseCurrency?.iso || "USD").toUpperCase();
    if (!normalized.some((item) => item.iso === baseIso)) {
      normalized.push({
        id: baseIso,
        iso: baseIso,
        name: getCurrencyName(baseIso),
        isFallbackBase: true,
      });
    }

    return normalized;
  }, [availableCurrencies, baseCurrency?.iso]);

  const preferredCurrency = useMemo(
    () =>
      currencyCatalog.find(
        (item) => item.iso === (baseCurrency?.iso || "USD").toUpperCase()
      ) || currencyCatalog[0] || null,
    [baseCurrency?.iso, currencyCatalog]
  );

  useEffect(() => {
    if (hasLoadedCurrencies && !selectedCurrencyId && preferredCurrency) {
      setSelectedCurrencyId(String(preferredCurrency.id));
    }
  }, [hasLoadedCurrencies, preferredCurrency, selectedCurrencyId]);

  const selectedCurrencyData = useMemo(
    () => currencyCatalog.find((item) => String(item.id) === String(selectedCurrencyId)),
    [currencyCatalog, selectedCurrencyId]
  );

  const isSelectedCurrencyBase =
    (selectedCurrencyData?.iso || "").toUpperCase() ===
    (baseCurrency?.iso || "USD").toUpperCase();

  const {
    rateResolution,
    isRateLoading,
    appliedExchangeRate,
  } = useExchangeRateForDate({
    currencyId: selectedCurrencyData?.id,
    isUsdCurrency: isSelectedCurrencyBase || Boolean(selectedCurrencyData?.isFallbackBase),
    date: currentDate,
    activeExchangeRate,
    condominiumId: selectedCondominium,
  });

  const convertedBalanceAmount = useMemo(
    () =>
      convertToUsd(
        balanceData.amount,
        isSelectedCurrencyBase,
        appliedExchangeRate
      ),
    [appliedExchangeRate, balanceData.amount, isSelectedCurrencyBase]
  );

  const rateHelpText = useMemo(() => {
    if (isSelectedCurrencyBase) return "";
    if (isRateLoading) return "Consultando tasa...";
    if (!appliedExchangeRate || !Number(balanceData.amount)) return "";

    const effectiveDateKey =
      rateResolution?.usedDateKey || rateResolution?.selectedDateKey;
    const rateDate = effectiveDateKey ? moment(effectiveDateKey) : moment();
    return `Tasa ${rateDate.format("DD/MM/YYYY")}: ${formatAmountRounded(
      appliedExchangeRate,
      2
    )} | Equivalente aproximado: ${baseCurrency?.symbol || "$"}${formatAmountRounded(
      convertedBalanceAmount,
      2
    )} ${baseCurrency?.iso || "USD"}`;
  }, [
    appliedExchangeRate,
    balanceData.amount,
    baseCurrency?.iso,
    baseCurrency?.symbol,
    convertedBalanceAmount,
    isRateLoading,
    isSelectedCurrencyBase,
    rateResolution,
  ]);

  const handleTogglePayment = (paymentId, isActive) => {
    Swal.fire({
      title: "¿Estás seguro?",
      text: isActive
        ? "Se desactivará este aporte recurrente."
        : "Se activará este aporte recurrente.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#9341af",
      cancelButtonColor: "#bbb",
      confirmButtonText: isActive ? "Si, desactivar" : "Si, activar",
      cancelButtonText: "Cancelar",
    }).then(async (result) => {
      if (!result.value) return;
      try {
        await toggleReserveFundPaymentStatus(paymentId, isActive ? 0 : 1);
        Toast.fire({
          type: "success",
          title: isActive
            ? "Aporte desactivado correctamente."
            : "Aporte activado correctamente.",
        });
        await mutate();
      } catch (error) {
        handleErrorMessage(error);
      }
    });
  };

  const handleEditAmount = (payment) => {
    setEditingPayment(payment);
  };

  const handleAddBalance = async () => {
    try {
      const amount = Number(balanceData.amount);
      if (!amount || amount <= 0) {
        Toast.fire({
          type: "error",
          title: "Por favor ingresa un monto válido.",
        });
        return;
      }

      if (!isSelectedCurrencyBase && !(Number(appliedExchangeRate) > 0)) {
        Toast.fire({
          type: "error",
          title: "No hay una tasa disponible para convertir este saldo a la moneda base.",
        });
        return;
      }

      const formData = new FormData();
      formData.append("condominium_id", id || selectedCondominium);
      formData.append("amount", convertedBalanceAmount);
      formData.append("description", balanceData.description || "Aporte manual");
      if (!isSelectedCurrencyBase) {
        formData.append("rate", appliedExchangeRate);
      }

      const response = await addReserveFundBalance(selectedFund.id, formData);
      if (response.code === SUCCESS_RESPONSE.CREATED) {
        Toast.fire({
          type: "success",
          title: "Saldo agregado satisfactoriamente.",
        });
        setBalanceModal(false);
        setSelectedFund(null);
        setBalanceData({ amount: "", description: "" });
        await mutate();
      }
    } catch (error) {
      handleErrorMessage(error);
    }
  };

  const handleActivateAllPayments = () => {
    Swal.fire({
      title: "¿Estás seguro?",
      text: "Se activarán todos los aportes recurrentes pausados de este condominio.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#9341af",
      cancelButtonColor: "#bbb",
      confirmButtonText: "Si, activar todos",
      cancelButtonText: "Cancelar",
    }).then(async (result) => {
      if (!result.value) return;
      try {
        const { code } = await activateAllReserveFundPayments(
          selectedCondominium
        );
        if (code === SUCCESS_RESPONSE.OK) {
          Toast.fire({
            type: "success",
            title: "Aportes recurrentes activados correctamente.",
          });
          await mutate();
        }
      } catch (error) {
        handleErrorMessage(error);
      }
    });
  };

  const filter_options = [
    { value: "", displayName: "Todos", disabled: false },
    { value: 1, displayName: "Activos", disabled: false },
    { value: 0, displayName: "Inactivos", disabled: false },
  ];

  const ButtonAdd = ({ onClick }) => {
    return (
      <ButtonIcon color="falcon-default" onClick={onClick} icon={faPlus}>
        Crear fondo de reserva
      </ButtonIcon>
    );
  };

  const { loading } = useContext(CurrencyContext);
  const { currency } = useSelector((state) => state.auth);

  const ButtonAddPayment = ({ onClick }) => {
    return (
      <ButtonIcon color="falcon-default" onClick={onClick} icon={faPlus}>
        Emitir cobro para fondo
      </ButtonIcon>
    );
  };

  const handleCreateReserveFund = () => {
    setRedirectUrl(`/dashboard/finance/${id}/reserve-funds/create`);
    setRedirect(true);
  };

  const handleCreatePaymentFund = () => {
    setRedirectUrl(`/dashboard/finance/${id}/payment-fund/create`);
    setRedirect(true);
  };

  const handleViewDetails = async (reserveFundId) => {
    setRedirectUrl(
      `/dashboard/finance/${id}/reserve-funds/${reserveFundId}`
    );
    setRedirect(true);
  };

  const handleDropdownAction = (action) => {
    if (action === "edit") {
      onEditAction(rest);
      return;
    }
    if (action === "delete") {
      return collapseOneOpen(true);
    }
    throw new Error("invalid action");
  };

  return (
    <>
      <PageHeader
        title="Fondos de reserva"
        titleTag="h2"
        description="Gestiona y administra cada uno de tus fondos."
        className="mb-3"
        col={{ lg: 9 }}
      >
        <SupportLink />
      </PageHeader>
      <Card>
        <FalconCardHeader title="Fondos de reserva">
          <Row>
            <Col>
              <Flex>
                <Filter
                  keyField={"active"}
                  options={filter_options}
                  selectType={setParams}
                  dateFilter={true}
                />
              </Flex>
            </Col>
          </Row>
          <hr />
          <Row className="justify-content-end">
            <Col xs="auto" lg="auto" md={12} sm={12} className="mt-3">
              <ButtonIcon
                color="falcon-success"
                onClick={handleActivateAllPayments}
                icon={faToggleOn}
                className="mr-2"
              >
                Activar todos
              </ButtonIcon>
              <ButtonAdd onClick={handleCreateReserveFund} />
            </Col>
          </Row>
        </FalconCardHeader>
        <CardBody className="fs--1 p-0">
          <PreloadState cond={isIterableArray(data)} loading={isLoading}>
            {data.length > 0 ? (
              <>
                {data.map((reserve, index) => (
                  <Fragment key={index}>
                    <Col className=" shadow-none border-top">
                      <Row className="mb-2 mt-2 align-items-center">
                        <Col xs={12} md={4} className="d-flex align-items-center mb-2 mb-md-0">
                          <div className="media-body">
                            <h6 className="mb-0 font-weight-semi-bold d-flex align-items-center">
                              <span className="text-900">{reserve.name}</span>
                              {reserve?.latest_payment && (
                                <Badge
                                  className="rounded-pill ml-2"
                                  color={
                                    reserve.latest_payment.is_active
                                      ? "success"
                                      : "secondary"
                                  }
                                >
                                  {reserve.latest_payment.is_active
                                    ? "Activo"
                                    : "Inactivo"}
                                </Badge>
                              )}
                            </h6>
                            <p className="m-0 text-truncate">{reserve.description}</p>
                            <p className="m-0 text-500">
                              creada el{" "}
                              {moment(reserve.created_at).format("DD-MM-YYYY")}
                            </p>
                          </div>
                        </Col>
                        <Col xs={12} md={7}>
                          <Row className="text-center text-md-right">
                            <Col xs={reserve?.pending_to_collect > 0 ? 4 : 6}>
                              <p className="text-500 mb-0">Balance del fondo</p>
                              <span className="d-block font-weight-semi-bold text-primary">
                                <NumberFormat
                                  prefix={`${baseCurrency.symbol} `}
                                  allowedDecimalSeparators={true}
                                  decimalScale={2}
                                  fixedDecimalScale={true}
                                  thousandSeparator={true}
                                  displayType="text"
                                  value={
                                    reserve?.balance === null
                                      ? 0
                                      : reserve?.balance
                                  }
                                />
                              </span>
                              {curr?.currency?.iso && (
                                <span className="d-block text-700 fs--2">
                                  <NumberFormat
                                    displayType="text"
                                    allowedDecimalSeparators={true}
                                    thousandSeparator={true}
                                    decimalScale={2}
                                    fixedDecimalScale
                                    prefix={curr.currency.iso + " "}
                                    value={(reserve?.balance || 0) * (curr?.value || 0)}
                                  />
                                </span>
                              )}
                            </Col>
                            <Col xs={reserve?.pending_to_collect > 0 ? 4 : 6}>
                              <p className="text-500 mb-0">Monto disponible</p>
                              <span className="d-block font-weight-semi-bold">
                                <NumberFormat
                                  prefix={`${baseCurrency.symbol} `}
                                  allowedDecimalSeparators={true}
                                  decimalScale={2}
                                  fixedDecimalScale={true}
                                  thousandSeparator={true}
                                  displayType="text"
                                  value={reserve?.money_available}
                                />
                              </span>
                              {curr?.currency?.iso && (
                                <span className="d-block text-700 fs--2">
                                  <NumberFormat
                                    displayType="text"
                                    allowedDecimalSeparators={true}
                                    thousandSeparator={true}
                                    decimalScale={2}
                                    fixedDecimalScale
                                    prefix={curr.currency.iso + " "}
                                    value={(reserve?.money_available || 0) * (curr?.value || 0)}
                                  />
                                </span>
                              )}
                            </Col>
                            {reserve?.pending_to_collect > 0 && (
                              <Col xs={4}>
                                <p className="text-500 mb-0">Pendiente por cobrar</p>
                                <span className="d-block font-weight-semi-bold text-warning">
                                  <NumberFormat
                                    prefix={`${baseCurrency.symbol} `}
                                    allowedDecimalSeparators={true}
                                    decimalScale={2}
                                    fixedDecimalScale={true}
                                    thousandSeparator={true}
                                    displayType="text"
                                    value={reserve.pending_to_collect}
                                  />
                                </span>
                                {curr?.currency?.iso && (
                                  <span className="d-block text-700 fs--2">
                                    <NumberFormat
                                      displayType="text"
                                      allowedDecimalSeparators={true}
                                      thousandSeparator={true}
                                      decimalScale={2}
                                      fixedDecimalScale
                                      prefix={curr.currency.iso + " "}
                                      value={(reserve?.pending_to_collect || 0) * (curr?.value || 0)}
                                    />
                                  </span>
                                )}
                              </Col>
                            )}
                          </Row>
                        </Col>
                        <Col xs="auto" md={1} className="d-flex align-items-center justify-content-end ml-auto">
                          <UncontrolledDropdown direction="left">
                            <DropdownToggle color="falcon-default" size="sm">
                              <FontAwesomeIcon icon="ellipsis-h" />
                            </DropdownToggle>
                            <DropdownMenu>
                              <DropdownItem
                                onClick={() => {
                                  handleViewDetails(reserve.id);
                                }}
                              >
                                Ver detalles
                              </DropdownItem>
                              {reserve?.latest_payment && (
                                <DropdownItem
                                  className={
                                    reserve.latest_payment.is_active
                                      ? "text-danger"
                                      : "text-success"
                                  }
                                  onClick={() =>
                                    handleTogglePayment(
                                      reserve.latest_payment.id,
                                      reserve.latest_payment.is_active
                                    )
                                  }
                                >
                                  {reserve.latest_payment.is_active
                                    ? "Desactivar aporte recurrente"
                                    : "Activar aporte recurrente"}
                                </DropdownItem>
                              )}
                              <DropdownItem
                                onClick={() => {
                                  setSelectedFund(reserve);
                                  setBalanceData({ amount: "", description: "" });
                                  setBalanceModal(true);
                                }}
                              >
                                Agregar saldo
                              </DropdownItem>
                              {reserve?.latest_payment?.is_active && (
                                <DropdownItem
                                  onClick={() =>
                                    handleEditAmount(reserve.latest_payment)
                                  }
                                >
                                  Editar monto
                                </DropdownItem>
                              )}
                            </DropdownMenu>
                          </UncontrolledDropdown>
                        </Col>
                      </Row>
                    </Col>
                  </Fragment>
                ))}
              </>
            ) : (
              <Row className="p-card">
                <Col>
                  <Alert color="info" className="mb-0">
                    Al parecer aún no hay fondos para mostrar. Puedes crear una
                    ahora mismo pulsando el siguiente enlace
                    <Button
                      data-testid="Button"
                      tag={Link}
                      color="link"
                      size="sm"
                      className="ml-1 pl-0"
                      to={`/dashboard/finance/${selectedCondominium}/reserve-funds/create`}
                    >
                      Crear fondo de reserva
                    </Button>
                  </Alert>
                </Col>
              </Row>
            )}
            {/*               <Or value={false} cond={error ? "error" : "ok"}>
                <Or value={"error"}>
                  <Row className="p-card">
                    <Col>
                      <Alert color="danger" className="mb-0">
                        Esta página no está disponible
                        <Button
                          data-testid="Button"
                          color="link"
                          size="sm"
                          className="ml-1 pl-0"
                          onClick={() => {
                            setRedirectUrl(`/dashboard/finance`);
                            setRedirect(true);
                          }}
                        >
                          Volver
                        </Button>
                      </Alert>
                    </Col>
                  </Row>
                </Or>
                <Or value={"ok"}>
                  
                </Or>
              </Or> */}
            {showPagination && (
              /*      <CardFooter className="border-top"> */
              <FullPagination
                nextPage={nextPage}
                prevPage={previousPage}
                goToPage={goToPage}
                meta={meta}
                className={"shadow-none"}
                posText={"Fondos de reserva"}
                perPage={perPage}
              />
              /*       </CardFooter> */
            )}
          </PreloadState>
        </CardBody>
      </Card>
      <Modal
        isOpen={balanceModal}
        toggle={() => setBalanceModal(!balanceModal)}
      >
        <ModalHeader toggle={() => setBalanceModal(!balanceModal)}>
          Agregar saldo a {selectedFund?.name}
        </ModalHeader>
        <ModalBody>
          <FormGroup>
            <Label>Monto a agregar</Label>
            <InputWithLeftSelect
              selectValue={selectedCurrencyId}
              onSelectChange={(event) => setSelectedCurrencyId(event.target.value)}
              selectOptions={currencyCatalog.map((item) => ({
                value: String(item.id),
                label: `${item.iso} (${item.name})`,
              }))}
              selectAriaLabel="Moneda del saldo"
              selectMinWidth="120px"
              helpText={rateHelpText}
            >
              <NumberFormat
                key={selectedCurrencyData?.id || selectedCurrencyData?.iso}
                value={balanceData.amount}
                thousandSeparator
                decimalScale={2}
                fixedDecimalScale
                allowNegative={false}
                className="form-control border-left-0"
                placeholder="0.00"
                prefix={`${resolveCurrencySymbol(
                  selectedCurrencyData,
                  baseCurrency
                )} `}
                onValueChange={({ value }) =>
                  setBalanceData((current) => ({ ...current, amount: value }))
                }
              />
            </InputWithLeftSelect>
          </FormGroup>
          <FormGroup>
            <Label>Descripción</Label>
            <Input
              type="text"
              value={balanceData.description}
              onChange={(event) =>
                setBalanceData((current) => ({
                  ...current,
                  description: event.target.value,
                }))
              }
              placeholder="Ej: Aporte extraordinario del condominio"
            />
          </FormGroup>
          {Number(balanceData.amount) > 0 && (
            <Card className="border-warning bg-soft-warning mb-3">
              <CardBody className="py-3">
                <div className="text-700 fs--1">
                  El monto que se guardará en el sistema será el equivalente en{" "}
                  {getCurrencyName(baseCurrency?.iso || "USD")} {baseCurrency?.symbol || "$"}. El valor mostrado es la referencia original ingresada por el usuario.
                </div>
              </CardBody>
            </Card>
          )}
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={() => setBalanceModal(false)}>
            Cancelar
          </Button>
          <Button color="primary" onClick={handleAddBalance}>
            Agregar saldo
          </Button>
        </ModalFooter>
      </Modal>
      <EditReserveFundAmountModal
        isOpen={Boolean(editingPayment)}
        toggle={() => setEditingPayment(null)}
        payment={editingPayment}
        condominiumId={id || selectedCondominium}
        onSuccess={mutate}
      />
    </>
  );
};

ReserveFund.propTypes = {
  showPagination: PropTypes.bool,
};

export default withRedirect(ReserveFund);
